import { history, undo, redo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { splitListItem } from 'prosemirror-schema-list';

import {
  baseKeymap,
  chainCommands,
  newlineInCode,
  createParagraphNear,
  liftEmptyBlock,
  splitBlock,
} from 'prosemirror-commands';
// TODO - the gap Cursor has a gapcursor.css that's required with it
import { gapCursor } from 'prosemirror-gapcursor'; // better cursor behavior
// import { dropCursor } from 'prosemirror-dropcursor'; // better cursor for drag and dropping
import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { EditorView, NodeView } from 'prosemirror-view';
import { inputRules } from 'prosemirror-inputrules';
import { suggestionModePlugin } from 'prosemirror-suggestion-mode';
import { Note } from '@annote/types';
import { createSuggestionReasonComponent, createButtonsComponent } from './hoverMenu';
import { schema } from './schema';
import { autocompletPlugin } from './suggestPlugin';
import { parser } from './markdown';
import { suggestLinkMenu } from './suggestLinkMenu';
import { rules } from './rules';
import { anniChatSelectorPlugin } from './anniChatSelectorPlugin';
// import linkSelectedPlugin from './linkSelectedPlugin';

const handleEnterForSuggestLinkMenu = (): boolean => {
  if (suggestLinkMenu.isShown()) {
    suggestLinkMenu.chooseSelected();
    return true;
  }
  return false;
};

// extra keys
const extraKeymap = {
  'Mod-z': undo,
  'Mod-y': redo,
  'Mod-Shift-z': redo,

  ArrowDown: () => {
    if (suggestLinkMenu.isShown()) {
      suggestLinkMenu.selectNext();
      return true;
    }
    return false;
  },
  ArrowUp: () => {
    if (suggestLinkMenu.isShown()) {
      suggestLinkMenu.selectPrevious();
      return true;
    }
    return false;
  },
  Enter: chainCommands(
    handleEnterForSuggestLinkMenu,
    newlineInCode,
    splitListItem(schema.nodes.list_item),
    createParagraphNear,
    liftEmptyBlock,
    splitBlock,
  ),

  Escape: () => {
    if (suggestLinkMenu.isShown()) {
      suggestLinkMenu.hide();
      return true;
    }
    return false;
  },
};

// returns a new editor view
interface CreateEditorOptions {
  plugins?: Plugin[];
  nodeViews?: Record<string, (node: unknown, view: EditorView, getPos: () => number | undefined) => NodeView>;
  handleKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean;
}

const createEditor = (
  container: HTMLElement,
  note: Note,
  options: CreateEditorOptions = {},
  readOnly = false,
  enableSuggestions = false,
  spellCheck = true,
): EditorView => {
  const doc = parser.parse(`${note.value}`) || undefined;

  const plugins: Plugin[] = [
    inputRules({ rules }),
    autocompletPlugin({ note }),
    keymap({ ...baseKeymap, ...extraKeymap }),
    history(),
    gapCursor(),
    anniChatSelectorPlugin({ note }),
  ];

  // Add any additional plugins from options
  if (options.plugins) {
    plugins.push(...options.plugins);
  }

  if (enableSuggestions) {
    plugins.push(
      suggestionModePlugin({
        username: 'Anni',
        hoverMenuOptions: {
          components: {
            createInfoComponent: createSuggestionReasonComponent,
            createButtonsComponent,
          },
        },
        inSuggestionMode: false, // don't start in suggestion mode
      }),
    );
  }
  const state = EditorState.create({ doc, plugins });

  // Build view options
  // EditorView automatically handles state updates - we only intercept for readOnly
  const viewOptions: {
    state: EditorState;
    nodeViews?: Record<string, (node: unknown, view: EditorView, getPos: () => number | undefined) => NodeView>;
    dispatchTransaction?: (transaction: Transaction) => void;
    handleKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean;
    editable?: () => boolean;
  } = {
    state,
    handleKeyDown: (v, event) => {
      // If read-only, block all key events
      if (readOnly) return true;

      // If custom handleKeyDown provided, try it first
      if (options.handleKeyDown) {
        const handled = options.handleKeyDown(v, event);
        if (handled) return true;
      }

      event.stopPropagation();
      return false;
    },
    editable: () => !readOnly,
  };

  // If readOnly, prevent transactions from updating state
  if (readOnly) {
    viewOptions.dispatchTransaction = (_transaction: Transaction): void => {
      // Block all state updates in read-only mode
      // EditorView will still maintain state but won't update from transactions
    };
  }

  // Add custom nodeViews if provided
  if (options.nodeViews) {
    viewOptions.nodeViews = options.nodeViews;
  }

  const view = new EditorView(container, viewOptions);

  // Add the "nodrag" class so react flow won't interact with it
  view.dom.classList.add('nodrag');

  // Set spellcheck attribute
  view.dom.setAttribute('spellcheck', spellCheck ? 'true' : 'false');

  if (readOnly) {
    view.dom.classList.add('read-only');
  }

  return view; // Returning the view may be useful for further manipulation
};

export { createEditor, EditorView };
export { setAnniChatHandler } from './anniChatSelectorPlugin';
export { highlightReferencesPlugin, updateHighlightDecorations } from './highlightReferencesPlugin';
export type { HighlightedReference } from './highlightReferencesPlugin';
